import { ComponentType, ReactNode } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { Button, makeStyles, SvgIconProps } from '@material-ui/core';
import LockIcon from '@material-ui/icons/LockOutlined';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import { LinkButton } from '@backstage/core-components';
import { CONTENT_MAX_WIDTH } from '../nav/layout';
import { useByxT } from '../shared/translations';
import { useCatalogRootPath } from '../shared/useCatalogRootPath';
import { spaceGrotesk } from '../theme/fonts';
import { byxScheme } from '../theme/tokens';
import { SearchOffIcon } from './SearchOffIcon';

/** Where "Voltar ao Início" leads: the home page at the app root. */
export const HOME_PATH = '/';

const useStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    // Outside any page layout (unknown routes): the page column with its
    // 24px gutters, as the page header keeps them.
    page: {
      maxWidth: CONTENT_MAX_WIDTH + theme.spacing(6),
      margin: '0 auto',
      padding: theme.spacing(3),
    },
    panel: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(8, 3),
      textAlign: 'center',
      backgroundColor: scheme.panel,
      border: `1px solid ${scheme.line}`,
      borderRadius: 14,
    },
    icon: {
      fontSize: 32,
      color: scheme.mutedText,
    },
    title: {
      margin: theme.spacing(1.5, 0, 0),
      fontFamily: spaceGrotesk,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: '22px',
      color: scheme.ink,
    },
    text: {
      maxWidth: 480,
      margin: theme.spacing(0.5, 0, 0),
      fontSize: 13,
      lineHeight: '19px',
      color: scheme.mutedText,
    },
    actions: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(2.5),
    },
    // Design system Primary button: 36px, label 14px.
    primary: {
      height: 36,
      padding: theme.spacing(0, 2),
      fontSize: 14,
    },
    // Design system Text button: 32px, label 13px in primary.
    textButton: {
      height: 32,
      padding: theme.spacing(0, 1.5),
      fontSize: 13,
      color: theme.palette.primary.main,
      '&:hover': { backgroundColor: scheme.hover },
    },
  };
});

type StatusPanelProps = {
  icon: ComponentType<SvgIconProps>;
  title: string;
  text?: string;
  /** h1 where the panel is the page (not found), h2 under a page header. */
  heading?: 'h1' | 'h2';
  role?: 'alert';
  children: ReactNode;
};

/**
 * The centred v2 panel of the not-found and no-permission states: a 32px
 * icon, the title, the text and the actions. No illustration and no
 * external link (UX "Página não encontrada / Sem permissão").
 */
function StatusPanel(props: Readonly<StatusPanelProps>) {
  const { icon: Icon, title, text, heading = 'h2', role, children } = props;
  const classes = useStyles();
  const Heading = heading;
  return (
    <section className={classes.panel} role={role}>
      <Icon aria-hidden className={classes.icon} />
      <Heading className={classes.title}>{title}</Heading>
      {text && <p className={classes.text}>{text}</p>}
      <div className={classes.actions}>{children}</div>
    </section>
  );
}

/**
 * Whether this tab has an earlier page of the portal to go back to
 * (CR9-9). The router keys the first entry of a tab `default`; the browser
 * router also keeps the entry's index in `history.state.idx`, which is 0
 * for the first entry even after a replace or a reload. Either means the
 * page was opened directly, and the browser back would leave the portal or
 * do nothing.
 */
export function hasEarlierPortalPage(
  location: Pick<Location, 'key'>,
  historyState: unknown = window.history.state,
): boolean {
  if (location.key === 'default') {
    return false;
  }
  const index = (historyState as { idx?: unknown } | null)?.idx;
  return typeof index !== 'number' || index > 0;
}

/**
 * Página não encontrada (U5-9, AC-30): replaces the stock 404 page for every
 * not-found use (unknown routes, TechDocs, the scaffolder editor guard),
 * inside the normal frame. "Voltar ao Início" opens Início; "Voltar" is the
 * browser back, shown only when there is an earlier portal page in this tab
 * (UX clarification, CR9-9).
 */
export function ByxNotFound() {
  const classes = useStyles();
  const { t } = useByxT();
  const navigate = useNavigate();
  const canGoBack = hasEarlierPortalPage(useLocation());
  return (
    <div className={classes.page}>
      <StatusPanel
        icon={SearchOffIcon}
        heading="h1"
        title={t('errorPages.notFound.title')}
        text={t('errorPages.notFound.text')}
      >
        <LinkButton
          to={HOME_PATH}
          variant="contained"
          color="primary"
          disableElevation
          className={classes.primary}
        >
          {t('errorPages.notFound.home')}
        </LinkButton>
        {canGoBack && (
          <Button
            variant="text"
            className={classes.textButton}
            onClick={() => navigate(-1)}
          >
            {t('errorPages.notFound.back')}
          </Button>
        )}
      </StatusPanel>
    </div>
  );
}

/**
 * Sem permissão para registrar (U5-9, AC-30): Registrar existente when the
 * permission check denies catalog entity creation. Presentation only: the
 * catalog backend enforces the same permission.
 */
export function ByxNoPermission() {
  const classes = useStyles();
  const { t } = useByxT();
  const catalogRoot = useCatalogRootPath();
  return (
    <StatusPanel
      icon={LockIcon}
      title={t('errorPages.noPermission.registerTitle')}
      text={t('errorPages.noPermission.registerText')}
    >
      <LinkButton
        to={catalogRoot}
        variant="contained"
        color="primary"
        disableElevation
        className={classes.primary}
      >
        {t('errorPages.noPermission.catalog')}
      </LinkButton>
    </StatusPanel>
  );
}

/**
 * The permission check itself failed (AC-30): not the Sem permissão text,
 * and "Tentar de novo" runs the check again.
 */
export function ByxPermissionCheckError(
  props: Readonly<{ onRetry: () => void }>,
) {
  const classes = useStyles();
  const { t } = useByxT();
  return (
    <StatusPanel
      icon={ErrorIcon}
      role="alert"
      title={t('errorPages.checkError.text')}
    >
      <Button
        variant="contained"
        color="primary"
        disableElevation
        className={classes.primary}
        onClick={props.onRetry}
      >
        {t('errorPages.checkError.retry')}
      </Button>
    </StatusPanel>
  );
}
